import { useMemo } from 'react';

import { FulfillmentType, TreezSpecials } from '@/types';
import { useDispensaryTimeZone } from '@/hooks';
import { resolveTimeZone } from '@/utils';
import { useFulFillmentType } from '@/hooks/useFulFillmentType';

import { SellTreezClient, useSellTreezClient } from '../api';

export class DiscountService {
  private _client: SellTreezClient;
  private _timeZone: string;
  private _fulfillmentType: FulfillmentType | undefined;

  constructor({
    client,
    timeZone,
    fulfillmentType,
  }: {
    client?: SellTreezClient;
    timeZone?: string;
    fulfillmentType?: FulfillmentType;
  }) {
    this._client = client ?? new SellTreezClient('');
    this._timeZone = resolveTimeZone(timeZone);
    this._fulfillmentType = fulfillmentType;
  }

  getDiscounts = async (): Promise<TreezSpecials[]> => {
    const discountSearchParams = {
      excludeInactive: 'true',
      ...(this._timeZone ? { timezone: this._timeZone } : {}),
      ...(this._fulfillmentType
        ? { fulfillmentType: this._fulfillmentType }
        : {}),
    };
    const result = await this._client.get<TreezSpecials[]>(
      `discount?${new URLSearchParams(discountSearchParams).toString()}`,
    );

    return result;
  };
}

interface Args {
  fulfillmentType?: FulfillmentType;
}

export const useDiscountService = (args: Args = {}) => {
  const { fulfillmentType: fulfillmentTypeProp } = args;
  const { fulfillmentType: fulfillmentTypeFromHook } = useFulFillmentType();
  const fulfillmentType = fulfillmentTypeProp || fulfillmentTypeFromHook;

  const client = useSellTreezClient();
  const timeZone = useDispensaryTimeZone();

  const discountService = useMemo(
    () =>
      new DiscountService({
        client,
        timeZone,
        fulfillmentType,
      }),
    [client, timeZone, fulfillmentType],
  );

  return discountService;
};
